<template>
	<SuiModalDialog
		class="resource-booking-modal"
		data-testid="resource-booking-modal"
	>
		<template #header>
			<h1>{{ modalTitle }}</h1>
		</template>

		<div class="resource-booking-modal__form">
			<div class="resource-booking-modal__form-header">
				<ResourcePlanningUserSelectDropdown
					class="resource-booking-modal__user-select"
					:is-user-selection-enabled="Boolean(bookingId)"
					:selected-entity-id="booking.selectedEntityId"
					:selected-entity-type="booking.selectedEntityType"
					:selected-entity-name="selectedEntityName"
					:selected-role-id="selectedRoleId"
					with-role-placeholders
					:project-id="projectId"
					@user-selected="onUserSelected"
					@role-selected="onRoleSelected"
					@role-initialized="onRoleInitialized"
				/>
				<div v-if="showProject" class="resource-booking-modal__project">
					<SuiIcon name="icon-briefcase-16" />
					{{ projectName }}
				</div>
			</div>
			<ResourcePlanningBookingModalDateInputs
				:booking="booking"
				:validator="v"
				:is-split-mode="isSplitMode"
				:is-start-end-date-disabled="isStartEndDateDisabled"
				:is-start-date-disabled="isStartDateDisabled"
				:is-end-date-disabled="isEndDateDisabled"
				:working-days="workingDays"
				:booking-duration-to-display="bookingDurationToDisplay"
				:split-date="splitDate"
				:is-split-date-disabled="isSplitDateDisabled"
				@update:start-date="onUpdateStartDate"
				@update:end-date="onUpdateEndDate"
				@update:split-date="onUpdateSplitDate"
				@update:booking-unit="onUpdateBookingUnit"
				@update:amount-percent="booking.amountPercent = $event"
				@update:amount-time="booking.amountTime = $event"
			/>
			<SuiInput
				v-model="booking.description"
				:label="$t('resource-booking__description')"
				:variant="v.description.$error ? 'error' : undefined"
				class="resource-booking-modal__description"
				@blur="validateInputField('description')"
			/>

			<ResourcePlanningBookingModalAllocationTypeSelection
				v-if="hasTentativeBookingsFeature"
				v-model="selectedAllocationType"
				:is-fixed-booking-disabled="isFixedBookingDisabled"
			/>
		</div>
		<template #footer>
			<ResourcePlanningBookingModalFooter
				:booking-id="bookingId"
				:is-split-mode="isSplitMode"
				:is-loading-delete="loadingDelete"
				:is-loading-saving="
					isLoadingSaveBooking || isLoadingSplitBooking
				"
				:is-save-button-disabled="isSaveButtonDisabled"
				:save-button-disabled-tooltip="saveButtonDisabledTooltip"
				@toggle-split="isSplitMode = !isSplitMode"
				@delete="onDeleteBooking"
				@cancel="modalClose"
				@save="onResourceSave"
			/>
		</template>
	</SuiModalDialog>
</template>
<script setup lang="ts">
import {
	ref,
	reactive,
	computed,
	toRefs,
	onMounted,
	onUnmounted,
	watch,
} from 'vue';
import { SuiIcon, SuiInput, SuiModalDialog } from '@developers/scoro-ui';
import { useFormatter, useTranslation } from '@/composables';
import {
	DEFAULT_BOOKING_AMOUNT,
	ZOOM_OPTIONS,
	BOOKING_ALLOCATION_TYPE,
} from '../../constants';
import {
	closePopOver,
	getRemainingAvailabilityDuration,
	getTotalBookingDuration,
	weekStartsOnSettingDateFnsMapping,
} from '../ResourcePlanningGantt/ResourcePlanningGanttUtils';
import { ResourcePlanningUserSelectDropdown } from '../ResourcePlanningUserSelect';
import {
	lastDayOfWeek,
	lastDayOfMonth,
	isBefore,
	addYears,
	addDays,
	isAfter,
	subYears,
	isWithinInterval,
} from 'date-fns';
import { resetTime, shortDuration } from '@/utils/formatting';
import { timeToSeconds } from '@/utils/time';
import {
	useBooleanSetting,
	useSettings,
} from '@/composables/settings/useSettings';
import { Setting, WEEK_STARTS_ON } from '@/constants/settings';
import {
	useResourceBookingDeleteMutation,
	useResourceBookingSplitMutation,
	BookingAmountType,
	BookingCreationSource,
	type User,
	type RoleProject,
	type Booking,
	type BookingSaveInput,
	type Project,
	TYPES,
	type Role,
} from '@/generated/graphql';
import { useMessage } from '@/features/Message';
import { SPLIT_MODE_MESSAGE_ID } from '../ResourcePlanningBookingSplit/constants';
import { useVuelidate } from '@vuelidate/core';
import { maxLength } from '@vuelidate/validators';
import {
	largerThanZeroTime,
	largerThanZeroNumber,
	required,
} from '@/utils/validators';
import { useAvailabilityService } from '@/features/Availability';
import { type ZoomLevelName, type BookingAllocationType } from '../../types';
import ResourcePlanningBookingModalAllocationTypeSelection from './ResourcePlanningBookingModalAllocationTypeSelection.vue';
import ResourcePlanningBookingModalDateInputs from './ResourcePlanningBookingModalDateInputs.vue';
import ResourcePlanningBookingModalFooter from './ResourcePlanningBookingModalFooter.vue';
import { useResourcePlanningUtils } from '@/features/ResourcePlanning/components/ResourcePlanningGantt/useResourcePlanningUtils';
import {
	BOOKING_DATE_YEARS_FORWARD,
	BOOKING_DATE_YEARS_BACK,
	BOOKING_DATE_RANGE_YEARS,
} from './constants';
import { type SAPIDate } from '@/types/sapi/scalars';

type BookingForm = {
	bookingId: string | undefined;
	// Can be null temporarily when switching to a role that's not a project member yet.
	selectedEntityId: User['id'] | RoleProject['id'] | null;
	selectedEntityType: typeof TYPES.User | typeof TYPES.RoleProject;
	startDate: SAPIDate;
	endDate: SAPIDate;
	amountTime: string;
	amountPercent: string;
	unit: BookingAmountType;
	hasSaveAttempt: boolean;
	isTentative: boolean;
	projectId: Project['id'];
	description: string;
};

// If the startDate passed we make the booking according to that date and gantt zoom level
// else we make the booking adjusted by projectStartDate
export type ResourcePlanningBookingModalProps = {
	projectId: Project['id'];
	startDate?: Date;
	endDate?: Date;
	zoomLevel?: ZoomLevelName;
	projectStartDate?: Date;
	projectName?: Project['name'];
	bookingId?: Booking['id'];
	selectedEntityId: User['id'] | RoleProject['id'];
	selectedEntityType: typeof TYPES.User | typeof TYPES.RoleProject;
	selectedEntityName?: Role['name'];
	amount?: Booking['amount'];
	amountType?: BookingAmountType;
	isTentative?: boolean;
	hasTentativeBookingsFeature?: boolean;
	showProject?: boolean;
	description?: Booking['description'];
	initialSplitMode?: boolean;
	initialSplitDate?: Date;
};

const dateToday = resetTime(new Date());
const dateAllowedMin = subYears(dateToday, BOOKING_DATE_YEARS_BACK);
const dateAllowedMax = addYears(dateToday, BOOKING_DATE_YEARS_FORWARD);

const props = defineProps<ResourcePlanningBookingModalProps>();

const {
	bookingId,
	projectId,
	selectedEntityId,
	selectedEntityType,
	selectedEntityName,
	startDate,
	endDate,
	amountType,
	amount,
	isTentative,
	description,
} = toRefs(props);

const emit = defineEmits<{
	(
		event: 'update-booking',
		input: BookingSaveInput,
		oldEntityId: User['id'] | RoleProject['id'],
		oldEntityType: typeof TYPES.RoleProject | typeof TYPES.User,
		selectedRoleId?: Role['id'] | null
	): void;
}>();

const { formatLongDuration, dateToMysqlString } = useFormatter();
const { t } = useTranslation();
const { setting } = useSettings();
const { loadAvailability } = useAvailabilityService();
const { getUserAvailability } = useAvailabilityService();
const { addErrorMessage, removeMessage } = useMessage();

const weekStartsOnSetting = computed(() =>
	Number(setting(Setting.WEEK_STARTS_ON) ?? WEEK_STARTS_ON.MONDAY)
);

const [includeWeekends] = useBooleanSetting(
	Setting.INCLUDE_WEEKENDS_WHEN_SCHEDULING
);

const bookingStartDate = computed(() => {
	if (!props.startDate && props.projectStartDate) {
		const projectStartDate = resetTime(props.projectStartDate);
		return resetTime(projectStartDate) <= resetTime(dateToday)
			? dateToday
			: projectStartDate;
	}
	return props.startDate ? resetTime(props.startDate) : dateToday;
});

const bookingEndDate = computed(() => {
	if (endDate?.value) {
		return endDate.value;
	}

	let tmpEndDate = resetTime(bookingStartDate.value);

	if (!startDate?.value) {
		tmpEndDate.setDate(tmpEndDate.getDate() + 6);
		return tmpEndDate;
	} else if (props.zoomLevel === ZOOM_OPTIONS.WEEK) {
		const weekStartsOn =
			weekStartsOnSettingDateFnsMapping[weekStartsOnSetting.value];

		tmpEndDate = lastDayOfWeek(bookingStartDate.value, { weekStartsOn });
	} else if (props.zoomLevel === ZOOM_OPTIONS.MONTH) {
		tmpEndDate = lastDayOfMonth(bookingStartDate.value);
	}

	return tmpEndDate;
});

const getFormattedDefaultBookingAmountPerDay = () =>
	shortDuration(DEFAULT_BOOKING_AMOUNT.TIME_PER_DAY)[0].value;

const isBookingTypePercent = computed(
	() => amountType?.value === BookingAmountType.Percent
);

const amountTime = computed(() => {
	if (!isBookingTypePercent.value && amount?.value) {
		return shortDuration(amount.value)[0].value;
	}

	return getFormattedDefaultBookingAmountPerDay();
});

const percentAmount = computed(() => {
	if (amount?.value && isBookingTypePercent.value) {
		return amount?.value;
	}

	return DEFAULT_BOOKING_AMOUNT.PERCENT;
});

const getBookingInitialValues = (): BookingForm => {
	return {
		bookingId: bookingId?.value,
		selectedEntityId: selectedEntityId.value,
		selectedEntityType: selectedEntityType.value,
		startDate: dateToMysqlString(bookingStartDate.value),
		endDate: dateToMysqlString(bookingEndDate.value),
		amountTime: amountTime.value,
		amountPercent: String(percentAmount.value),
		unit: amountType?.value ?? BookingAmountType.Hour,
		hasSaveAttempt: false,
		isTentative: isTentative.value,
		projectId: projectId.value,
		description: description?.value ?? '',
	};
};

const booking = reactive<BookingForm>({ ...getBookingInitialValues() });

const selectedAllocationType = computed<BookingAllocationType>({
	get: () =>
		booking.isTentative
			? BOOKING_ALLOCATION_TYPE.TENTATIVE
			: BOOKING_ALLOCATION_TYPE.FIXED,
	set: (value) => {
		booking.isTentative = value === BOOKING_ALLOCATION_TYPE.TENTATIVE;
	},
});

const isFixedBookingDisabled = computed(
	() => booking.selectedEntityType === TYPES.RoleProject
);

const isStartEndDateDisabled = computed(() => {
	if (!bookingStartDate.value) {
		return false;
	}
	return isBefore(bookingStartDate.value, dateAllowedMin);
});

const isDateWithinAllowedMinMaxDates = (date: Date): boolean => {
	return !isBefore(date, dateAllowedMin) && !isAfter(date, dateAllowedMax);
};

const isStartDateValid = (date: SAPIDate): boolean => {
	const startDate = resetTime(date);
	return isDateWithinAllowedMinMaxDates(startDate);
};

const isEndDateValid = (date: SAPIDate): boolean => {
	const endDate = resetTime(date);
	const startDate = resetTime(booking.startDate);

	if (!isDateWithinAllowedMinMaxDates(endDate)) {
		return false;
	}

	return endDate >= startDate;
};

const isStartDateDisabled = (date: SAPIDate): boolean => {
	return !isStartDateValid(date);
};

const isEndDateDisabled = (date: SAPIDate): boolean => {
	return !isEndDateValid(date);
};

const validateStartDate = (date: SAPIDate): boolean => {
	const startDate = resetTime(date);
	const endDate = resetTime(booking.endDate);

	if (!isDateWithinAllowedMinMaxDates(startDate) || startDate > endDate) {
		return false;
	}

	return isWithinInterval(startDate, {
		start: subYears(endDate, BOOKING_DATE_RANGE_YEARS),
		end: addYears(endDate, BOOKING_DATE_RANGE_YEARS),
	});
};

const validateEndDate = (date: SAPIDate): boolean => {
	const endDate = resetTime(date);
	const startDate = resetTime(booking.startDate);

	if (!isDateWithinAllowedMinMaxDates(endDate)) {
		return false;
	}

	if (endDate < startDate) {
		return false;
	}

	return isWithinInterval(endDate, {
		start: subYears(startDate, BOOKING_DATE_RANGE_YEARS),
		end: addYears(startDate, BOOKING_DATE_RANGE_YEARS),
	});
};

const rules = computed(() => {
	const rules = {
		startDate: { validateStartDate },
		endDate: { validateEndDate },
		amountPercent: {},
		amountTime: {},
		description: { maxLength: maxLength(255) },
	};

	if (booking.unit === BookingAmountType.Percent) {
		rules.amountPercent = { required, largerThanZeroNumber };
	} else {
		rules.amountTime = { largerThanZeroTime };
	}

	return rules;
});

const v = useVuelidate(rules, booking, { $rewardEarly: true });

const validateInputField = (field: string) => {
	v.value[field].$touch();
	v.value[field].$commit();
};

watch(
	() => booking.unit,
	(val: BookingAmountType) => {
		if (val === BookingAmountType.Percent) {
			validateInputField('amountPercent');
		} else {
			validateInputField('amountTime');
		}
	}
);

const modalClose = () => {
	Object.assign(booking, { ...getBookingInitialValues() });
	closePopOver();
};

const isSplitMode = ref(props.initialSplitMode ?? false);

const selectedRoleId = ref<Role['id'] | null>(null);

const modalTitle = computed(() => {
	if (isSplitMode.value) {
		return t('resource-booking__split-booking');
	}
	return bookingId?.value
		? t('resource-booking__modify-booking')
		: t('resource-booking__new-booking');
});

const initialSplitDate = props.initialSplitDate
	? dateToMysqlString(resetTime(props.initialSplitDate))
	: dateToMysqlString(addDays(resetTime(bookingStartDate.value), 1));

const splitDate = ref(initialSplitDate);

const isSplitDateDisabled = (date: SAPIDate): boolean => {
	const resetDate = resetTime(date);
	return (
		!isAfter(resetDate, resetTime(bookingStartDate.value)) ||
		isAfter(resetDate, resetTime(bookingEndDate.value))
	);
};

const onUpdateSplitDate = (date: SAPIDate) => {
	if (date) {
		splitDate.value = date;
	}
};

const onUserSelected = async (id: User['id']) => {
	if (id) {
		booking.selectedEntityId = id;
		booking.selectedEntityType = TYPES.User;
		selectedRoleId.value = null;
	}
};

const onRoleSelected = (
	roleId: Role['id'],
	roleProjectId: RoleProject['id'] | undefined
) => {
	selectedRoleId.value = roleId;
	booking.selectedEntityId = roleProjectId ?? null;
	booking.selectedEntityType = TYPES.RoleProject;
	booking.isTentative = true;
};

const onRoleInitialized = (roleId: Role['id'] | null) => {
	selectedRoleId.value = roleId;
};

const checkAndMoveEndDate = (endDate: Date, startDate: Date) => {
	if (resetTime(endDate) < resetTime(startDate)) {
		booking.endDate = booking.startDate;
	}
};

const onUpdateStartDate = (startDate: SAPIDate) => {
	if (startDate) {
		booking.startDate = startDate;
		checkAndMoveEndDate(resetTime(booking.endDate), resetTime(startDate));
	}

	validateInputField('startDate');

	if (!validateStartDate(booking.startDate)) {
		addErrorMessage({
			message: t('resource_planning_start_date_invalid'),
		});
	}
};

const onUpdateEndDate = (endDate: SAPIDate) => {
	if (endDate) {
		booking.endDate = endDate;
		checkAndMoveEndDate(resetTime(endDate), resetTime(booking.startDate));
	}

	validateInputField('endDate');

	if (!validateEndDate(booking.endDate)) {
		addErrorMessage({
			message: t('resource_planning_end_date_invalid'),
		});
	}
};

const onUpdateBookingUnit = (unit: BookingAmountType) => {
	if (unit && unit !== booking.unit) {
		booking.unit = unit;

		const isTotalUnitType = unit === BookingAmountType.Total;

		if (isTotalUnitType) {
			const selectedUserId =
				booking.selectedEntityType === TYPES.User &&
				booking.selectedEntityId
					? booking.selectedEntityId
					: '';
			const userAvailability = getUserAvailability(selectedUserId);

			const newBookingAmountTime = shortDuration(
				getRemainingAvailabilityDuration({
					startDate: resetTime(booking.startDate),
					endDate: resetTime(booking.endDate),
					resourceType: booking.selectedEntityType,
					userAvailability,
					includeWeekends: includeWeekends.value,
				}).availability
			)[0].value;

			booking.amountTime = newBookingAmountTime;
		}

		const isHourUnitType = unit === BookingAmountType.Hour;

		if (isHourUnitType) {
			const newBookingAmountTime =
				getFormattedDefaultBookingAmountPerDay();

			booking.amountTime = newBookingAmountTime;
		}
	}
};

const totalBookingDuration = computed(() => {
	const startDate = resetTime(booking.startDate);
	const endDate = resetTime(booking.endDate);
	const selectedUserId =
		booking.selectedEntityType === TYPES.User && booking.selectedEntityId
			? booking.selectedEntityId
			: '';
	const userAvailability = getUserAvailability(selectedUserId);

	const { duration, daysUsed } = getTotalBookingDuration({
		startDate,
		endDate,
		bookingType: booking.unit,
		bookedDuration: timeToSeconds(booking.amountTime),
		bookedPercent: Number(booking.amountPercent),
		userAvailability,
		resourceId: booking.selectedEntityId ?? null,
		resourceType: booking.selectedEntityType,
		includeWeekends: includeWeekends.value,
	});

	return {
		duration: formatLongDuration({
			duration,
			removeMinutesOnFullHours: true,
		}),
		daysUsed,
	};
});

const workingDays = computed(() => totalBookingDuration.value.daysUsed);

const perDayBookingDuration = computed(() => {
	if (booking.unit !== BookingAmountType.Total) {
		return;
	}

	let durationPerDay = 0;

	if (workingDays.value > 0) {
		durationPerDay = timeToSeconds(booking.amountTime) / workingDays.value;
	}

	return formatLongDuration({
		duration: durationPerDay,
		removeMinutesOnFullHours: true,
	});
});

const bookingDurationToDisplay = computed(() =>
	booking.unit === BookingAmountType.Total
		? perDayBookingDuration.value
		: totalBookingDuration.value.duration
);

const { saveBookingMutation, isLoadingSaveBooking } =
	useResourcePlanningUtils();
const { mutate: deleteBooking, loading: loadingDelete } =
	useResourceBookingDeleteMutation({});
const { mutate: splitBooking, loading: isLoadingSplitBooking } =
	useResourceBookingSplitMutation({});

const isNewBookingTooEarly = computed(
	() => !booking.bookingId && isStartDateDisabled(booking.startDate)
);

const isSaveButtonDisabled = computed(() => {
	const hasValidEntity =
		booking.selectedEntityId || selectedRoleId.value !== null;

	return (
		isLoadingSaveBooking.value ||
		!hasValidEntity ||
		v.value.$errors.length > 0 ||
		isNewBookingTooEarly.value
	);
});

const saveButtonDisabledTooltip = computed(() => {
	if (isNewBookingTooEarly.value) {
		return t('resource_planning_start_date_invalid');
	}

	return undefined;
});

const onResourceSave = async () => {
	if (isSplitMode.value && booking.bookingId) {
		let userIdForSplit: User['id'] | null = null;
		let roleProjectIdForSplit: RoleProject['id'] | null = null;

		if (booking.selectedEntityType === TYPES.User) {
			userIdForSplit = booking.selectedEntityId;
		} else if (booking.selectedEntityType === TYPES.RoleProject) {
			roleProjectIdForSplit = booking.selectedEntityId;
		}

		const res = await splitBooking({
			input: {
				bookingId: booking.bookingId,
				splitDate: splitDate.value,
				description: booking.description,
				isTentative: booking.isTentative,
				userId: userIdForSplit,
				roleProjectId: roleProjectIdForSplit,
				roleId: selectedRoleId.value,
			},
		});

		if (res?.errors) {
			return;
		}

		removeMessage(SPLIT_MODE_MESSAGE_ID);
		modalClose();
		return;
	}

	const input: BookingSaveInput = {
		id: booking.bookingId,
		projectId: projectId.value,
		amountType: booking.unit,
		userId:
			booking.selectedEntityType === TYPES.User
				? booking.selectedEntityId
				: null,
		roleProjectId:
			booking.selectedEntityType === TYPES.RoleProject &&
			booking.selectedEntityId !== null
				? booking.selectedEntityId
				: null,
		roleId: selectedRoleId.value,
		amount:
			booking.unit === BookingAmountType.Percent
				? parseInt(booking.amountPercent)
				: timeToSeconds(booking.amountTime),
		startDate: booking.startDate,
		endDate: booking.endDate,
		isTentative: booking.isTentative,
		description: booking.description || null,
		creationSource: props.showProject
			? BookingCreationSource.BookingsModule
			: BookingCreationSource.ProjectBookings,
	};

	const isMovingExistingBooking =
		bookingId.value &&
		(booking.selectedEntityId !== selectedEntityId.value ||
			booking.selectedEntityType !== selectedEntityType.value);

	if (isMovingExistingBooking) {
		emit(
			'update-booking',
			input,
			selectedEntityId.value,
			selectedEntityType.value,
			selectedRoleId.value
		);

		return;
	}

	const res = await saveBookingMutation(input);

	if (res?.errors) {
		return;
	}

	booking.bookingId = res?.data?.BookingSave.id ?? '';

	modalClose();
};

const onDeleteBooking = async () => {
	const response = await deleteBooking({
		input: {
			id: props.bookingId,
		},
	});

	if (response?.errors) {
		addErrorMessage({
			message: t('resource-booking__delete-failed'),
		});

		return;
	}
	modalClose();
};

onMounted(() => {
	if (booking.selectedEntityId && booking.selectedEntityType === TYPES.User) {
		loadAvailability(
			resetTime(booking.startDate),
			resetTime(booking.endDate),
			[booking.selectedEntityId]
		);
	}

	const background = document.createElement('div');

	background.className = 'resource-booking-modal__background';
	background.addEventListener('click', modalClose);

	document.body.appendChild(background);
});

onUnmounted(() => {
	const background = document.querySelector(
		'.resource-booking-modal__background'
	);
	background?.remove();
});
</script>

<style lang="scss" scoped>
.resource-booking-modal {
	--sui-modal-dialog-max-width: max-content;

	&__form {
		display: flex;
		flex-direction: column;
		gap: 2.5rem;
	}

	&__form-header {
		display: flex;
		gap: 1rem;
	}

	&__project {
		font-size: 1.625rem;
		color: $neutral-90;
		display: flex;
		align-items: center;
		gap: 0.5rem;
		height: 4rem;
	}

	&__description {
		width: 42rem;
	}
}
</style>

<style lang="scss">
.resource-booking-modal__background {
	z-index: z('modal-popup');
	inset: 0;
	position: fixed;
}
</style>
